import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import styled from "styled-components";

import { useTranslation } from "../../../hooks/useTranslation";
import { useStyles } from "../../../context/StyleContext";
import { useCheckout } from "../../../context/CheckoutContext";
import {
  fetchGatewayConnection,
  processPayment,
  createCheckoutComApplePaySession,
} from "../../../api/api";
import { fetchSession, updateSession } from "../../../utils/paymentSession";
import { mapStylesToCheckoutFlow } from "../../../utils/checkoutComStyleMapper";
import { useCheckoutApplePay } from "../../../hooks/useCheckoutApplePay";
import { safeRedirect } from "../../../utils/redirectUrlBuilder";
import { formatPaymentError } from "../../../utils/formatPaymentError";
import { handleCitPaymentApiResponse, resolveCitRedirectUrl } from "../../../utils/paymentOutcome";

// Shared DOM id — OrderSummary renders a <div> with this id in place of the
// "Pay and Subscribe" button when Apple Pay is the selected method. The CKO
// Flow Apple Pay button mounts here (Stripe-style: the Pay button *is* the
// Apple Pay button).
const APPLE_PAY_SLOT_ID = "altruon-applepay-slot";

const InfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 8px;
`;

const StatusMessage = styled.p`
  margin: 0;
  color: ${(p) =>
    p.$variant === "error"
      ? p.$styles?.components?.errorMessage?.color || "#d14343"
      : p.$styles?.colors?.textSecondary || "#4b5563"};
  font-size: ${(p) => p.$styles?.typography?.fontSizes?.md || "14px"};
  font-family: ${(p) => p.$styles?.typography?.fontFamily || "Arial, sans-serif"};
  text-align: center;
`;

const Spinner = styled.div`
  width: 28px;
  height: 28px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${(p) => p.$styles?.colors?.primary || "#2563eb"};
  border-radius: 50%;
  animation: applepay-spin 1s linear infinite;

  @keyframes applepay-spin {
    0%   { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const detectEnvironment = (publicKey) => {
  if (!publicKey) return "sandbox";
  return publicKey.startsWith("pk_sbox_") || publicKey.includes("sandbox")
    ? "sandbox"
    : "production";
};

/**
 * Hosted checkout Apple Pay form (Checkout.com).
 *
 * Responsibilities:
 *   1. Fetch the gateway connection (public key) for the selected
 *      applepay/checkoutcom routing entry.
 *   2. Create a CKO payment session via the backend.
 *   3. Mount the CKO Flow Apple Pay button into the slot exposed by
 *      OrderSummary (#altruon-applepay-slot), replacing the default
 *      "Pay and Subscribe" button.
 *   4. On CKO onPaymentCompleted, submit the payment to the Altruon
 *      backend (processPayment) and handle redirect / success / failure.
 *
 * Unlike the SDK/embedded variant (AltruonCheckoutComApplePayForm), no
 * postMessage bridge is needed: the hosted page IS the top window, so we
 * can run CheckoutWebComponents directly.
 */
const CheckoutComApplePayForm = ({
  formData,
  itemDetails,
  billingPlatformId,
  routingEntries,
  planId,
  platformItemDetails,
  selectedAddons,
  prefetchedGatewayConnection,
  sessionId,
}) => {
  const { t } = useTranslation();
  const { styles } = useStyles();
  const { clientIp, clientCountry, estimatedOrder, isHostedPageMode } = useCheckout();

  const [gatewayConnection, setGatewayConnection] = useState(prefetchedGatewayConnection || null);
  const [paymentSession, setPaymentSession] = useState(null);
  const [initializing, setInitializing] = useState(true);
  const [initError, setInitError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const isProcessingRef = useRef(false);

  const applePayRouting = useMemo(
    () =>
      routingEntries?.find(
        (e) => e.paymentMethod === "applepay" && e.platform === "checkoutcom"
      ),
    [routingEntries]
  );

  const publicKey = gatewayConnection?.credentials?.public_key;
  const environment = useMemo(() => detectEnvironment(publicKey), [publicKey]);

  // Initialize gateway connection + CKO payment session.
  useEffect(() => {
    if (!applePayRouting || !sessionId) return;

    let cancelled = false;
    const init = async () => {
      try {
        setInitializing(true);
        setInitError(null);

        let connection = gatewayConnection;
        if (!connection) {
          connection = await fetchGatewayConnection(applePayRouting, t);
          if (cancelled) return;
          setGatewayConnection(connection);
        }

        if (!connection?.credentials?.public_key) {
          throw new Error("Missing public_key on checkoutcom gateway connection");
        }

        const session = await createCheckoutComApplePaySession({
          sessionId,
          itemDetails,
          formData,
          clientCountry,
          platformItemDetails,
          selectedAddons,
          estimatedOrder,
        });
        if (cancelled) return;

        if (!session?.payment_session_token) {
          throw new Error("CKO did not return a payment_session_token");
        }
        setPaymentSession(session);
      } catch (err) {
        if (cancelled) return;
        console.error("[ApplePay] init error", err);
        setInitError(err?.message || t("messages.error.somethingWrong"));
      } finally {
        if (!cancelled) setInitializing(false);
      }
    };

    init();
    return () => {
      cancelled = true;
    };
    // We intentionally exclude formData/itemDetails from deps — the CKO session
    // is created once per checkout; re-keying per field change would thrash the
    // CKO SDK. We still pass the latest formData in the processPayment payload.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applePayRouting, sessionId]);

  // On CKO onPaymentCompleted, submit the payment to Altruon backend.
  const handlePaymentCompleted = useCallback(
    async (component, paymentResponse) => {
      if (isProcessingRef.current) return;
      isProcessingRef.current = true;
      setProcessing(true);
      setPaymentError(null);

      try {
        const latestSession = (await fetchSession(sessionId)) || {};

        const payload = {
          paymentData: {
            gatewayConnectionId:
              gatewayConnection?.id ||
              latestSession?.paymentData?.gatewayConnectionId,
            gatewayName: "checkoutcom",
            paymentMethod: "applepay",
            transactionId: "",
            additionalData: {
              payment_method_flow: "DIRECT",
              paymentId: paymentResponse?.id,
            },
          },
          customerData: {
            firstName: formData?.firstName || latestSession?.customerData?.firstName,
            lastName: formData?.lastName || latestSession?.customerData?.lastName,
            email: formData?.email || latestSession?.customerData?.email,
            phone: formData?.phone || latestSession?.customerData?.phone,
            ip: clientIp || "127.0.0.1",
            billingAddress: {
              country:
                formData?.billingAddress?.country ||
                latestSession?.customerData?.billingAddress?.country ||
                clientCountry ||
                "US",
              zipCode:
                formData?.billingAddress?.zipCode ||
                latestSession?.customerData?.billingAddress?.zipCode ||
                "",
              street:
                formData?.billingAddress?.street ||
                latestSession?.customerData?.billingAddress?.street ||
                "",
              city:
                formData?.billingAddress?.city ||
                latestSession?.customerData?.billingAddress?.city ||
                "",
              state:
                formData?.billingAddress?.state ||
                latestSession?.customerData?.billingAddress?.state ||
                "",
            },
          },
          billingData: {
            planId,
            billingPlatformId,
            billingPlatformName:
              itemDetails?.billingPlatformName ||
              latestSession?.billingData?.billingPlatformName ||
              "",
            frequency: latestSession?.billingData?.frequency || "",
          },
        };

        await updateSession(sessionId, payload);
        const res = await processPayment(billingPlatformId, sessionId, payload);
        const citUi = { isHostedPageMode, t, setError: setPaymentError };
        const { handled, parsed } = handleCitPaymentApiResponse(res, citUi);
        if (handled) return;

        if (res.status === 200) {
          const transactionId =
            parsed.transactionId ||
            res?.data?.newTransactionId ||
            res?.data?.transactionId ||
            paymentResponse?.id;
          const baseRedirect = resolveCitRedirectUrl(res.data);
          const redirectUrl =
            baseRedirect &&
            `${baseRedirect}${baseRedirect.includes("?") ? "&" : "?"}trxId=${transactionId}`;

          if (redirectUrl) {
            safeRedirect(redirectUrl, { target: "self" });
            return;
          }

          const fallbackRedirect = res?.data?.checkoutRedirectUrl;
          if (fallbackRedirect) {
            safeRedirect(fallbackRedirect, { target: "self" });
          }
        } else {
          throw new Error(
            formatPaymentError(res?.data?.message, t)
          );
        }
      } catch (err) {
        console.error("[ApplePay] payment error", err);
        if (
          err?.response &&
          handleCitPaymentApiResponse(err.response, {
            isHostedPageMode,
            t,
            setError: setPaymentError,
          }).handled
        ) {
          return;
        }
        const message = formatPaymentError(
          err?.response?.data?.message || err?.message,
          t
        );
        setPaymentError(message);
      } finally {
        isProcessingRef.current = false;
        setProcessing(false);
      }
    },
    [
      sessionId,
      gatewayConnection,
      formData,
      clientIp,
      planId,
      billingPlatformId,
      itemDetails,
      t,
      isHostedPageMode,
    ]
  );

  const {
    applePayComponent,
    ready,
    isAvailable,
    loading: sdkLoading,
    error: sdkError,
    mount,
    unmount,
  } = useCheckoutApplePay({
    publicKey,
    paymentSession,
    environment,
    appearance: mapStylesToCheckoutFlow(styles),
    onPaymentCompleted: handlePaymentCompleted,
    onError: (message) => setPaymentError(message),
  });

  // Mount CKO Apple Pay button into OrderSummary's slot.
  // The slot is rendered conditionally by OrderSummary when Apple Pay is the
  // selected method, so we poll briefly in case the slot hasn't been painted yet.
  useEffect(() => {
    if (!applePayComponent) return undefined;

    let cancelled = false;
    let attempts = 0;

    const tryMount = async () => {
      while (!cancelled && attempts < 40) {
        const el = document.getElementById(APPLE_PAY_SLOT_ID);
        if (el) {
          await mount(el);
          return;
        }
        attempts += 1;
        await new Promise((r) => setTimeout(r, 50));
      }
    };

    tryMount();

    return () => {
      cancelled = true;
      unmount();
    };
  }, [applePayComponent, mount, unmount]);

  const displayError = paymentError || sdkError || initError;

  if (processing) {
    return (
      <InfoContainer>
        <Spinner $styles={styles} />
        <StatusMessage $styles={styles}>
          {t("checkout.paymentMethods.applepay.processing") ||
            "Processing Apple Pay payment…"}
        </StatusMessage>
      </InfoContainer>
    );
  }

  if (initializing || sdkLoading) {
    return (
      <InfoContainer>
        <Spinner $styles={styles} />
        <StatusMessage $styles={styles}>
          {t("checkout.paymentMethods.applepay.initializing") ||
            "Preparing Apple Pay…"}
        </StatusMessage>
      </InfoContainer>
    );
  }

  if (displayError) {
    return (
      <InfoContainer>
        <StatusMessage $styles={styles} $variant="error">
          {displayError}
        </StatusMessage>
      </InfoContainer>
    );
  }

  if (ready && !isAvailable) {
    return (
      <InfoContainer>
        <StatusMessage $styles={styles} $variant="error">
          {t("checkout.paymentMethods.applepay.unavailable") ||
            "Apple Pay is not available on this device. Please use Safari on an Apple device with a saved card."}
        </StatusMessage>
      </InfoContainer>
    );
  }

  return (
    <InfoContainer>
      <StatusMessage $styles={styles}>
        {t("checkout.paymentMethods.applepay.prompt") ||
          "Use the Apple Pay button below to complete your purchase."}
      </StatusMessage>
    </InfoContainer>
  );
};

export default CheckoutComApplePayForm;
